import React from 'react';
import { Card, Button, Tag, Descriptions, Space, Typography, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Soup, Exchange, Filter } from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import { FILTER_LABELS } from '../../utils/constants';

const { Title, Text } = Typography;

interface FiatCardProps {
  soup: Soup;
  fiatName: string;
  totalSoupsCount: number;
  onEditSoup: (soup: Soup) => void;
  onDeleteSoup: (soupId: number) => void;
  onEditExchange: (exchange: Exchange) => void;
  onDeleteExchange: (exchangeId: number) => void;
  onEditFilter: (filter: Filter, filterType: 'BUY' | 'SELL') => void;
}

const FiatCard: React.FC<FiatCardProps> = ({
  soup,
  fiatName,
  totalSoupsCount,
  onEditSoup,
  onDeleteSoup,
  onEditExchange,
  onDeleteExchange,
  onEditFilter,
}) => {
  const { editingItem } = useDataContext();

  const isEditingDisabled = editingItem !== null;

  const renderFilterInfo = (exchange: Exchange) => {
    const renderFilterDetails = (filter: Filter | undefined, type: string) => {
      if (!filter) return null;

      const items = [
        {
          key: 'month_order',
          label: FILTER_LABELS.month_order,
          children: filter.month_order,
        },
        {
          key: 'month_finish_rate',
          label: FILTER_LABELS.month_finish_rate,
          children: `${filter.month_finish_rate}`,
        },
        {
          key: 'max_low_single_trans_amount',
          label: FILTER_LABELS.max_low_single_trans_amount,
          children: filter.max_low_single_trans_amount,
        },
        {
          key: 'min_high_single_trans_amount',
          label: FILTER_LABELS.min_high_single_trans_amount,
          children: filter.min_high_single_trans_amount,
        },
        {
          key: 'average_size',
          label: FILTER_LABELS.average_size,
          children: filter.average_size,
        },
      ];

      return (
        <div key={type} style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>{type.toUpperCase() === 'BUY' ? 'Покупка' : 'Продажа'}</Text>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              disabled={isEditingDisabled}
              onClick={() => onEditFilter(filter, type as 'BUY' | 'SELL')}
            />
          </Space>
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 1, md: 2 }}
            items={items}
            style={{ marginTop: 8 }}
          />
        </div>
      );
    };

    return (
      <div>
        {renderFilterDetails(exchange.filter_buy, 'buy')}
        {renderFilterDetails(exchange.filter_sell, 'sell')}
      </div>
    );
  };

  const renderExchange = (exchange: Exchange) => (
    <Card
      key={exchange.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong>{exchange.exchange.name}</Text>
          </Col>
          <Col>
            <Space>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isEditingDisabled || soup.exchanges.length <= 1}
                onClick={() => onDeleteExchange(exchange.id)}
                title={soup.exchanges.length <= 1 ? "Нельзя удалить единственную биржу в супе" : "Удалить биржу"}
              />
            </Space>
          </Col>
        </Row>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>Банки:</Text>
        </div>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {exchange.banks.length > 0 ? (
            exchange.banks.map((bank) => (
              <Tag key={bank.id} color="blue">
                {bank.name}
              </Tag>
            ))
          ) : (
            <Text type="secondary">Банки не выбраны</Text>
          )}
          {/* Dashed plus tag to edit banks */}
          <Tag
            onClick={() => !isEditingDisabled && onEditExchange(exchange)}
            style={{
              borderStyle: 'dashed',
              cursor: isEditingDisabled ? 'not-allowed' : 'pointer',
              color: isEditingDisabled ? '#999' : '#1890ff'
            }}
          >
            <PlusOutlined />
          </Tag>
        </div>
      </div>

      <div>
        <Text strong>Фильтры:</Text>
        <div style={{ marginTop: 8 }}>
          {(exchange.filter_buy || exchange.filter_sell) ? (
            renderFilterInfo(exchange)
          ) : (
            <Text type="secondary">Фильтры не настроены</Text>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={0}>
              <Title level={4} style={{ margin: 0 }}>
                {soup.name}
              </Title>
              <Text type="secondary">
                Среднее значение: <Text strong>{soup.average}</Text>
              </Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="text"
                icon={<EditOutlined />}
                disabled={isEditingDisabled}
                onClick={() => onEditSoup(soup)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isEditingDisabled || totalSoupsCount <= 1}
                onClick={() => onDeleteSoup(soup.id)}
                title={totalSoupsCount <= 1 ? "Нельзя удалить единственный суп в валюте" : "Удалить суп"}
              />
            </Space>
          </Col>
        </Row>
      }
      style={{ marginBottom: 16 }}
    >
      <div>
        <Text strong>Биржи ({soup.exchanges.length}):</Text>
        <div style={{ marginTop: 12 }}>
          {soup.exchanges.length > 0 ? (
            soup.exchanges.map(renderExchange)
          ) : (
            <Text type="secondary">Биржи не добавлены</Text>
          )}
        </div>
      </div>
    </Card>
  );
};

export default React.memo(FiatCard);
